/**
 * The organization the signed-in person is currently working in.
 *
 * Someone who belongs to several stores — a chain's regional manager, a buying
 * group operating its members' sites — picks one in the shell. The choice is
 * per browser *and* per Firebase uid, and is sent as `X-Organization-Id` on
 * every call; the API checks it against their membership and answers 403 if
 * they are not a member.
 *
 * Admin impersonation is a different mechanism and always wins server-side, so
 * the header is left off entirely while an admin is impersonating.
 */

/** @deprecated Pre-uid shared key; cleared on write so a later user cannot inherit it. */
export const ACTING_ORGANIZATION_KEY = 'acting-organization';

export const actingOrganizationStorageKey = (uid: string): string =>
  `acting-organization:${uid}`;

const isImpersonating = (): boolean => {
  try {
    const saved = window.localStorage.getItem('admin-impersonation');
    if (!saved) return false;
    return Boolean(JSON.parse(saved)?.isImpersonating);
  } catch {
    return false;
  }
};

/** The chosen organization id for this viewer, or `null` when they have not switched. */
export const getActingOrganizationId = (uid: string | null | undefined): string | null => {
  if (!uid || typeof window === 'undefined' || !window.localStorage) return null;
  try {
    const value = window.localStorage.getItem(actingOrganizationStorageKey(uid));
    return value ? value : null;
  } catch {
    // Private browsing or blocked storage: no choice, which means the
    // account's primary organization, exactly as before.
    return null;
  }
};

export const setActingOrganizationId = (
  uid: string | null | undefined,
  organizationId: string | null,
): void => {
  if (!uid || typeof window === 'undefined' || !window.localStorage) return;
  try {
    // Drop the legacy shared key so a later account on this browser cannot
    // inherit the previous viewer's choice.
    window.localStorage.removeItem(ACTING_ORGANIZATION_KEY);
    if (organizationId) {
      window.localStorage.setItem(actingOrganizationStorageKey(uid), organizationId);
    } else {
      window.localStorage.removeItem(actingOrganizationStorageKey(uid));
    }
  } catch {
    // Best effort; the session simply stays on the primary organization.
  }
};

/** Drop this viewer's choice (logout / membership no longer valid). */
export const clearActingOrganizationId = (uid: string | null | undefined): void => {
  setActingOrganizationId(uid, null);
};

/** The header every authenticated call carries, when one has been chosen. */
export const getActingOrganizationHeaders = (
  uid?: string | null,
): Record<string, string> => {
  if (typeof window === 'undefined' || !window.localStorage) return {};
  if (isImpersonating()) return {};
  const organizationId = getActingOrganizationId(uid);
  return organizationId ? { 'X-Organization-Id': organizationId } : {};
};
